import React, { useEffect, useState } from 'react';
import type { CompanyProps, LocationProps, MyPlayerProps, PlayerBalanceProps, PollProps } from "../../src/types"
import { Button, Text, View } from "../../src/Components/Themed"
import { FlashMarketApi } from '../../src/FlashMarket/api';
import GlobalFlashModal from './components/GlobalFlashModal';
import LocationHolder from '../market/components/LocationHolder';
import { Icons } from '../../src/Components';
import { useColors } from '../../src/constants/useColors';
import { BEClarity, Components, FlashHolder } from 'be-components';
import moment from 'moment-mini';
import { ActivityIndicator, Image } from 'react-native';
import MarketHeader from '../market2/components/MarketHeader';
import type { BEClarityProps } from '../../src/Clarity';

type FlashWidgetProps = {
    company:CompanyProps,
    mode:'desktop'|'mobile',
    layout: 'card'|'banner',
    clarity?:BEClarityProps,
    inline?:boolean,
    insets: { top:number, bottom:number, left:number, right:number },
    me?:MyPlayerProps,
    flash_campaign_ids:string[],
    flash_ids:string[]
    my_balance?:PlayerBalanceProps,
    onShowWallet:() => void,
    onShowProfile:() => void,
    onRequestAuthenticate:() => void,
}

const FlashWidget = ({ company, layout, inline, clarity, flash_campaign_ids, flash_ids, me, my_balance, mode, insets, onRequestAuthenticate, onShowProfile, onShowWallet }:FlashWidgetProps) => {
    const Colors = useColors();
    const [ state, setState ] = useState<{
        loading:boolean,
        loaded:boolean,
        polls:PollProps[],
        flash: {
            visible:boolean,
            poll_id?:string,
            default_poll_id?:string
        }
    }>({
        loaded:false,
        loading: false,
        polls:[],
        flash: {
            visible: false
        }
    });
    const { loaded, flash, polls } = state;
    const [ location_state, setLocation ] = useState<{
        visible: boolean,
        location?:{
            location: LocationProps,
            region:string,
            coordinates:{ latitude:number, longitude:number, [key:string]:number }
        }
    }>({
        visible:false
    });

    const sorted_polls = polls.filter(p => ['active','paused'].includes(p.status)).sort((a,b) => {
        let a_status = a.status == 'active' ? 1 : 0
        let b_status = b.status == 'active' ? 1 : 0
        return b_status - a_status || moment(a.end_datetime).unix() - moment(b.end_datetime).unix()
    });

    useEffect(() => {
        FlashMarketApi.setEnvironment();
        getData(company.company_id);
    },[company.company_id, me?.player_id]);

    const getData = async(company_id:string) => {
        setState({ ...state, loading:true });
        let flash_state:{ visible:boolean, default_poll_id?:string, poll_id?:string } = { visible: false }
        const pc = await FlashMarketApi.getActiveMarkets(company_id);
        let s_polls = pc.polls
        if(flash_campaign_ids.length > 0){
            s_polls = s_polls.filter(p => flash_campaign_ids.includes(p.poll_campaign_id))
        }
        if(flash_ids.length > 0){
            s_polls = s_polls.filter(p => flash_ids.includes(p.poll_id));
        }
        if(inline){
            flash_state.default_poll_id = s_polls[0]?.poll_id
            //flash_state.poll_id = s_polls[0]?.poll_id
        }
        setState({
            ...state,
            polls: s_polls,
            flash: flash_state,
            loading: false,
            loaded:true
        });
    }

    const renderPolls = (data:{ item:PollProps, index:number, horizontal:boolean }) => {
        if(data.horizontal){
            return (
                <View transparent style={{ flexDirection:'row', alignItems:'center', padding:10, borderRightWidth:1, borderColor:Colors.borders.light }}>
                    {data.item.poll_image?.url ? 
                    <Image
                        source={{ uri: data.item.poll_image.url }}
                        style={{ height:35, width:35 }}
                        resizeMode='cover'
                    />
                    :
                    <Icons.FlameIcon size={28} color={Colors.text.error}/>
                    }
                    <View transparent style={{ width: 200, marginLeft:10, marginRight:10 }}>
                        <Text theme='h2'>{data.item.poll_question}</Text>
                    </View>
                    <Button
                        type={data.item.status == 'active' ? 'success' : 'action'}
                        title={data.item.status == 'active' ? 'BUY' : 'VIEW'}
                        style={{ padding:10 }}
                        onPress={() => {
                            if(BEClarity){ BEClarity.smartEvent('launch_market') }
                            setState({
                                ...state,
                                flash: {
                                    visible: true,
                                    default_poll_id: data.item.poll_id
                                }
                            })
                        }}
                    />
                </View>
            )
        }
        return (
            <View transparent style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                {data.item.poll_image?.url ? 
                <Image
                    source={{ uri: data.item.poll_image.url }}
                    style={{ height:40, width:40 }}
                    resizeMode='cover'
                />
                :
                <Icons.FlameIcon size={40} color={Colors.text.error}/>
                }
                <View transparent style={{ flex:1, marginLeft:10, marginRight:10 }}>
                    <Text theme='h1'>{data.item.poll_question}</Text>
                </View>
                <Button
                    type={data.item.status == 'active' ? 'success' : 'action'}
                    title={data.item.status == 'active' ? 'BUY' : 'VIEW'}
                    onPress={() => {
                        setState({
                            ...state,
                            flash: {
                                visible: true,
                                default_poll_id: data.item.poll_id
                            }
                        })
                    }}
                />
            </View>
        )
    }

    const renderFlash = () => {
        switch(layout){
            case 'banner':
                return (
                    <View transparent style={{ flex:1, flexDirection:'row' }}>
                        <View transparent style={{ padding:5, justifyContent:'center', alignItems:'center' }}>
                            <Icons.BettorEdgeIcon size={16} color={Colors.text.h1} />
                        </View>
                        <Components.AutoScrollingFlatList
                            data={sorted_polls}
                            speed={10}
                            horizontal
                            renderItem={({ item, index }) => renderPolls({ item, index, horizontal:true })}
                            key='popular_list'
                        />
                    </View>
                )
            case 'card':
                return (
                    <View float transparent style={{ flex:1, minHeight:400 }}>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                            <View transparent style={{ marginRight:10, justifyContent:'center', alignItems:'center' }}>
                                <Icons.BettorEdgeIcon size={16} color={Colors.text.h1} />
                            </View>
                            <View transparent style={{ flex:1, padding:5 }}>
                                <Text theme='h1'>Flash Markets</Text>
                                <Text theme='description' style={{ marginTop:3 }}>Lines powered by BettorEdge</Text>
                            </View>
                        </View>
                        <View transparent type='body' style={{ flex:1 }}>
                            <Components.AutoPageFlatList
                                time_on_page={10}
                                itemHeight={110}
                                reset_key={'NONE'}
                                data={sorted_polls}
                                renderItem={({ item, index }) => renderPolls({ item, index, horizontal: false })}
                            />
                        </View>
                    </View>
                )
            default: return <></>
        }
    }
    if(inline){
        return (
            <View style={{ flex:1 }}>
                <MarketHeader 
                    me={me}
                    header_style={{ borderTopRightRadius:8, borderTopLeftRadius:8 }}
                    my_balance={my_balance}
                    mode={mode}
                    company={company}
                    onShowProfile={() => {
                        onShowProfile()
                    }}
                    onShowWallet={() => {
                        onShowWallet()
                    }}
                    onRequestAuthenticate={() => onRequestAuthenticate()}
                />
                {loaded ?
                    <View>
                        <FlashHolder
                            company_id={company.company_id}
                            player={me}
                            wrap_options={true}
                            location={location_state.location?.location}
                            restricted_flash_ids={flash_ids}
                            default_poll_id={flash.default_poll_id}
                            poll_id={flash.poll_id}
                            init_expanded={true}
                            player_id={me?.player_id}
                            onRequestAuthenticate={onRequestAuthenticate}
                            onRequestLocation={() => {
                                setLocation({ ...location_state, visible: true });
                            }}
                        />
                    </View>
                    :
                    <ActivityIndicator style={{ padding:10, alignSelf:'center'}} size='large' />
                }
                <LocationHolder
                    player_id={me?.player_id}
                    onClose={() => {
                        setLocation({ ...location_state, visible:false });
                    }}
                    visible={location_state.visible}
                    mode={mode}
                    onLocationUpdate={(location) => {
                        if(!location_state.visible){ return }
                        let visible = true
                        if(location?.location?.legal_ind && location_state.visible){ visible = false }
                        setLocation({
                            visible,
                            location: {
                                location: location.location,
                                region: location.region,
                                coordinates: location.coordinates
                            }
                        });
                    }}
                />
            </View>
        )
    }

    return (
        <View style={{flex:1}}>
            {renderFlash()}
            <GlobalFlashModal
                me={me}
                my_balance={my_balance}
                mode={mode}
                restricted_flash_ids={flash_ids}
                default_poll_id={flash.default_poll_id}
                poll_id={flash.poll_id}
                company={company}
                onShowProfile={onShowProfile}
                location={location_state.location?.location}
                onShowWallet={onShowWallet}
                onRequestAuthenticate={onRequestAuthenticate}
                onRequestLocation={() => {
                    if(BEClarity){ BEClarity.smartEvent('show_location') }
                    setLocation({ ...location_state, visible: true });
                }}
                onClose={() => setState({ ...state, flash: { visible:false } })}
                visible={flash.visible}
                insets={insets}
            />
            <LocationHolder
                player_id={me?.player_id}
                onClose={() => {
                    setLocation({ ...location_state, visible:false });
                }}
                visible={location_state.visible}
                mode={mode}
                onLocationUpdate={(location) => {
                    if(!location_state.visible){ return }
                    let visible = true
                    if(location?.location?.legal_ind && location_state.visible){ visible = false }
                    if(location.location && BEClarity){
                        BEClarity.setCustomTag('be_location', location.location.value);
                    }
                    setLocation({
                        visible,
                        location: {
                            location: location.location,
                            region: location.region,
                            coordinates: location.coordinates
                        }
                    });
                }}
            />
        </View>
    )
}

export default FlashWidget